import { Transition } from "@headlessui/react";
import cx from "classnames";
import React, {
  FC,
  HTMLProps,
  ReactNode,
  createContext,
  useContext,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTimeout } from "usehooks-ts";
import DynamicHeroIcon, { HeroIcons } from "./DynamicHeroIcon";

export type ToastOptions = {
  intent: ToastIntent;
  icon?: HeroIcons;
  message: string | ReactNode;
  showIcon?: boolean;
  showCloseBtn?: boolean;
  autoClose?: number;
};

export interface ToastContextProps {
  open: (toastOptions: ToastOptions) => void;
}

export const ToastContext = createContext<ToastContextProps | null>(null);

export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export type ToastIntent = "success" | "error" | "custom" | "information";

export type ToastState = {
  id: string;
} & ToastOptions;

export interface ToastProviderOptions {
  children: ReactNode;
  autoClose?: number;
  position?: ToastPosition;
}

export const useToast = () => {
  return useContext(ToastContext);
};

export const ToastProvider: FC<ToastProviderOptions> = ({
  children,
  autoClose = Infinity,
  position = "bottom-left",
}) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const open = ({ autoClose = 5000, ...restToastOptions }: ToastOptions) =>
    setToasts((currentToasts) => [
      ...currentToasts,
      { id: generateUEID(), autoClose, ...restToastOptions },
    ]);
  const close = (id: string) =>
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id)
    );

  const contextValue = React.useMemo(() => ({ open }), []);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div className={cx("pointer-events-none fixed inset-4 z-50", {})}>
          <div
            className={cx("pointer-events-auto absolute", {
              "left-0 top-0": position === "top-left",
              "right-0 top-0": position === "top-right",
              "bottom-0 left-0": position === "bottom-left",
              "bottom-0 right-0": position === "bottom-right",
            })}
          >
            {toasts.map((toast) => (
              <Transition
                key={toast.id}
                appear
                show={true}
                className="my-4"
                enter="transition-all duration-150"
                enterFrom="opacity-0 scale-50"
                enterTo="opacity-100 scale-100"
                leave="transition-all duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-75"
              >
                <Toast
                  autoClose={autoClose}
                  {...toast}
                  // intent={toast.intent}
                  closeCallback={() => close(toast.id)}
                  // message={toast.content}
                />
              </Transition>
            ))}
          </div>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

interface ToastProps extends ToastOptions, HTMLProps<HTMLDivElement> {
  closeCallback?: () => void;
  message: string | ReactNode;
  autoClose: number;
}

const Toast: React.FC<ToastProps> = ({
  closeCallback,
  showCloseBtn = true,
  showIcon = true,
  message,
  className,
  intent,
  icon,
  autoClose,
}) => {
  const hideCallback = React.useCallback(() => {
    closeCallback && autoClose !== Infinity ? closeCallback() : null;
  }, [autoClose]);

  useTimeout(hideCallback, autoClose);

  let finalIcon = React.useMemo<HeroIcons>(() => {
    let icon;
    switch (intent) {
      case "success":
        icon = "CheckIcon";
        break;
      case "error":
        icon = "ExclamationCircleIcon";
        break;
      case "information":
        icon = "InformationCircleIcon";
        break;
      default:
        icon = "InformationCircleIcon";
        break;
    }
    return icon as HeroIcons;
  }, [intent]);

  return (
    <div
      className={cx(
        className,
        "flex w-full flex-row gap-x-4 rounded-lg bg-gray-800 p-3",
        {
          "items-center": typeof message === "string",
          "items-start": typeof message !== "string",
        }
      )}
    >
      {showIcon && icon && intent === "custom" ? (
        <div className="flex rounded-lg bg-gray-700 p-2 text-gray-200">
          <DynamicHeroIcon className="h-4 w-4" icon={icon} />
        </div>
      ) : null}

      {showIcon && intent !== "custom" ? (
        <div
          className={cx("flex rounded-lg p-2", {
            "bg-green-900 text-green-400 ": intent === "success",
            "bg-red-900 text-red-400": intent === "error",
            "bg-gray-700 text-gray-200": intent === "information",
          })}
        >
          <DynamicHeroIcon
            type="outline"
            className="h-4 w-4 stroke-2"
            icon={finalIcon}
          />
        </div>
      ) : null}
      <div
        className={cx("flex h-full w-full", {
          "items-center": typeof message === "string",
          "items-start": typeof message !== "string",
        })}
      >
        {typeof message === "string" ? (
          <p className="text-sm text-gray-300">{message}</p>
        ) : (
          <>{message}</>
        )}
      </div>
      {showCloseBtn && closeCallback ? (
        <div className="flex">
          <button
            type="button"
            className="-mx-1.5 -my-1.5 ml-auto inline-flex h-8 w-8 rounded-lg bg-gray-800 p-1.5 text-gray-500 hover:bg-gray-700 hover:text-white focus:ring-2  focus:ring-gray-600 "
            aria-label="Close"
            onClick={closeCallback}
          >
            <span className="sr-only">Close</span>
            <DynamicHeroIcon
              type="outline"
              className="h-5 w-5 stroke-2"
              icon={"XMarkIcon"}
            />
          </button>
        </div>
      ) : null}
    </div>
  );
};

function generateUEID() {
  let first = (Math.random() * 46656) | 0;
  let second = (Math.random() * 46656) | 0;

  return (
    ("000" + first.toString(36)).slice(-3) +
    ("000" + second.toString(36)).slice(-3)
  );
}

export default Toast;
